import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LoanType } from '@shared/enum';
import { ApplicationService } from '@shared/service/application.service';
import { Subscription } from 'rxjs';
import { StatefulService } from '@app/service/stateful.service';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { ApplicationApiService } from '@workflow/ecp-workflow/dynamic-app/service/application-api/application-api.service';
import { UserInfo } from '@workflow/ecp-shared/model/userinfo';
import { UserSessionService } from '@shared/service/user-session.service';

@Component({
  selector: 'cu-user-dropdown',
  styleUrls: ['./user-dropdown.component.scss'],
  templateUrl: './user-dropdown.component.html'
})
export class UserDropdownComponent implements OnInit, OnDestroy {
  public userMenuControl = `<i class="cu-user-circle bl-padding--16"></i><span class="bl-screen-reader-only">User menu</span>`;
  constructor(
    private readonly router: Router,
    private readonly userService: UserService,
    public readonly applicationApiService: ApplicationApiService,
    private readonly appService: ApplicationService,
    private readonly statefulService: StatefulService,
    private readonly sessionService: UserSessionService
  ) {}

  ngOnInit(): void {
    const authSub = this.userService.authSub.subscribe((isAuthenticated) => {
      // console.log('isAuthenticated', isAuthenticated);
      this.isAuthenticated = isAuthenticated;
    });
    const userSub = this.userService.userSub.subscribe((user) => {
      this.user = user;
      this.handleDisplayId();
    });

    this.subscriptions.add(userSub);
    this.subscriptions.add(authSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  // TODO: All authentication logic should be hoisted to parent component and passed as inputs.
  public isAuthenticated = false;
  public subscriptions = new Subscription();
  public user: UserInfo;
  public displayId: string;

  public handleDisplayId(): void {
    if (this.user && this.isAuthenticated) {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      if (this.user.isCellPhoneUser) {
        // eslint-disable-next-line @typescript-eslint/dot-notation
        let mobileNumber = this.user['cellPhone'];
        if (mobileNumber.length === 10) {
          mobileNumber = `(${mobileNumber.substring(0, 3)}) ${mobileNumber.substring(3, 6)}-${mobileNumber.substring(6)}`;
        }
        this.displayId = mobileNumber;
      } else {
        this.displayId = this.user.email;
      }
    } else {
      // if (!this.user) {
      //   console.warn('No user found');
      // } else {
      //   console.warn('User is not authenticated');
      // }
      this.displayId = null;
    }
  }

  /** Logout functionality clearing local storage and redirected to home page */
  /** Should not be making a to promise call here, the service should be logging out */
  public logout(): void {
    if (this.isPIDInProgress()) return;

    const isAPICallProgress = this.statefulService.wrap('mem:isAPICallProgress').value as boolean;
    if (!isAPICallProgress) {
      this.proceedToLogout();
    } else {
      this.statefulService.wrap('mem:isLogOutPending').value = true;
    }
    this.appService.invalidateCache();
  }

  public get showAccountlink(): boolean {
    if (!this.user) {
      return false;
    }
    return this.user.registeredWithSSO;
  }

  private proceedToLogout(): void {
    this.sessionService.logOut();
  }

  private isPIDInProgress(): boolean {
    const isIdentity = window.location.pathname.includes('/identity');
    const isPIDCompleted = this.statefulService.wrap('api:oaoMemberInformation/isIdentityVerificationCompleted').value;
    const isPIDAvailable = this.statefulService.wrap('api:oaoMemberInformation/oaoLenderSettings/isPIDQuestionsAvailable').value;
    const bundleValue = this.statefulService.wrap('api:oaoMemberInformation/isMidAppDecisionEnabled').value;
    const enableMidappDecision = typeof bundleValue === 'boolean' ? bundleValue : true;
    if (!enableMidappDecision && !isPIDCompleted && isPIDAvailable && isIdentity) {
      this.statefulService.wrap('mem:preventPIDVerificationExit').value = true;
      const subscription = this.userService.confrimToLeave$.subscribe((result) => {
        this.statefulService.wrap('mem:preventPIDVerificationExit').value = false;
        if (result) {
          this.statefulService.wrap('api:oaoMemberInformation/isIdentityVerificationCompleted').value = true;
          this.saveAndLogout();
        }
        subscription && subscription.unsubscribe();
      });
      return true;
    }

    return false;
  }

  public saveAndLogout(): void {
    const isAPICallProgress = this.statefulService.wrap('mem:isAPICallProgress').value as boolean;
    if (!isAPICallProgress) {
      const storagePrefix = this.userService.UserInfo.loginType === LoanType.OLA ? 'ola' : 'api';
      this.applicationApiService.updateBundleDataToDDMS(storagePrefix, this.logout.bind(this), this.logout.bind(this));
    } else {
      this.statefulService.wrap('mem:isLogOutPending').value = true;
    }
    this.appService.invalidateCache();
  }

  public goToDashboard(): void {
    this.router.navigateByUrl(`/consumer/dashboard?return=dashboard`);
  }

  // Evaluate if string is greater than maximum character length (default 25)
  // TODO: should this be a shared method?
  public isMaxChar(value: string, maxChar = 25): boolean {
    if (!value) return;
    return value.trim().length > maxChar;
  }
}
